import { useState } from "react";
import { ArrowRight, BookOpen, Users, Award, Clock } from "lucide-react";
import { LDA_LOGO } from "@/lib/assets";

const courses = [
  {
    title: "Advanced Endodontic Practice",
    level: "Advanced",
    format: "Hands-On + Lecture",
    cpd: "16 CPD Hours",
    desc: "Master complex root canal anatomy, rotary instrumentation, and microsurgical endodontic techniques. Includes phantom head and clinical observation.",
    tags: ["Rotary Endo", "Microsurgery", "Complex Cases"],
  },
  {
    title: "Dental Implants & Prosthodontics",
    level: "Intermediate–Advanced",
    format: "Surgical + Prosthodontic",
    cpd: "24 CPD Hours",
    desc: "Comprehensive implant training from treatment planning through to final restoration. Covers Straumann, Nobel, and Zimmer systems with live surgical sessions.",
    tags: ["Surgical Protocol", "Prosthetics", "Live Surgery"],
    featured: true,
  },
  {
    title: "Restoring Dental Implants",
    level: "Intermediate",
    format: "Hands-On Workshop",
    cpd: "12 CPD Hours",
    desc: "Focus on the restorative phase: crowns, bridges, and overdentures on implants. Includes impression techniques, bite registration, and final seating.",
    tags: ["Crowns", "Bridges", "Overdentures"],
  },
  {
    title: "Aesthetic Zone & Posterior Implants",
    level: "Advanced",
    format: "Masterclass",
    cpd: "20 CPD Hours",
    desc: "Advanced placement techniques in the aesthetic zone and posterior regions. Covers maxillary sinus augmentation, immediate loading, and ridge augmentation.",
    tags: ["Sinus Lift", "Aesthetic Zone", "Ridge Augmentation"],
  },
];

export default function AcademySection({ onBook }) {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section
      id="academy"
      data-testid="academy-section"
      className="py-24 md:py-32"
      style={{ background: '#082025' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">

        {/* Header */}
        <div className="reveal mb-16">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px" style={{ background: '#1CB0C6' }} />
            <span className="font-outfit text-xs tracking-[0.25em] uppercase" style={{ color: '#1CB0C6' }}>
              London Dental Academy
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              {/* LDA Logo */}
              <div className="mb-4 w-52 h-14 overflow-hidden" style={{ background: '#082025' }}>
                <img src={LDA_LOGO} alt="London Dental Academy" className="w-full h-full object-contain logo-screen" />
              </div>
              <h2
                className="font-cormorant font-light leading-tight"
                style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', color: '#F0FBFC' }}
              >
                Train Alongside a Consultant.<br />
                <em style={{ color: '#1CB0C6' }}>Raise your standard.</em>
              </h2>
            </div>
            <p
              className="font-outfit text-sm leading-relaxed max-w-xs"
              style={{ color: '#83A9AF' }}
            >
              Dr. Romeed personally delivers every course. No associates, no delegates — direct access to a consultant with four Royal College Fellowships and a PhD.
            </p>
          </div>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
          {courses.map((course, i) => (
            <div
              key={course.title}
              data-testid={`course-card-${i}`}
              className="reveal group transition-all duration-300"
              style={{
                background: hoveredCard === i ? '#0C2E35' : '#0A2830',
                border: `1px solid ${hoveredCard === i ? '#1CB0C6' : '#164B55'}`,
                padding: '32px',
                boxShadow: hoveredCard === i ? '0 0 30px -8px rgba(28,176,198,0.25)' : 'none',
                animationDelay: `${i * 0.1}s`,
              }}
              onMouseEnter={() => setHoveredCard(i)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {course.featured && (
                <div
                  className="inline-block font-outfit text-xs tracking-widest uppercase px-2 py-0.5 mb-4"
                  style={{ background: '#1CB0C6', color: '#082025' }}
                >
                  Flagship Course
                </div>
              )}
              <div className="flex items-start justify-between gap-4 mb-4">
                <h3
                  className="font-cormorant text-2xl font-medium leading-tight"
                  style={{ color: '#F0FBFC' }}
                >
                  {course.title}
                </h3>
                <span
                  className="font-outfit text-xs px-2 py-1 shrink-0 border"
                  style={{ borderColor: '#1CB0C6', color: '#1CB0C6' }}
                >
                  {course.level}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <span className="flex items-center gap-1.5 font-outfit text-xs" style={{ color: '#83A9AF' }}>
                  <BookOpen size={13} strokeWidth={1.5} />
                  {course.format}
                </span>
                <span className="flex items-center gap-1.5 font-outfit text-xs" style={{ color: '#83A9AF' }}>
                  <Clock size={13} strokeWidth={1.5} />
                  {course.cpd}
                </span>
              </div>

              <p
                className="font-outfit text-sm leading-relaxed mb-5"
                style={{ color: '#83A9AF' }}
              >
                {course.desc}
              </p>

              <div className="flex gap-2 flex-wrap">
                {course.tags.map((tag) => (
                  <span
                    key={tag}
                    className="font-outfit text-xs px-2 py-0.5"
                    style={{ background: '#164B55', color: '#1CB0C6' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Features bar */}
        <div
          className="reveal grid grid-cols-1 sm:grid-cols-3 gap-px mb-16"
          style={{ border: '1px solid #164B55', background: '#164B55' }}
        >
          {[
            { icon: <Users size={20} strokeWidth={1.5} />, title: "Small Cohorts", desc: "Maximum 8 delegates per course for hands-on access and direct mentorship." },
            { icon: <Award size={20} strokeWidth={1.5} />, title: "CPD Accredited", desc: "All courses are fully CPD-certified and relevant to GDC development outcomes." },
            { icon: <BookOpen size={20} strokeWidth={1.5} />, title: "Course Materials", desc: "Comprehensive notes, protocols, and post-course clinical support included." },
          ].map((feat) => (
            <div
              key={feat.title}
              className="flex flex-col gap-3 px-8 py-8"
              style={{ background: '#082025' }}
            >
              <div style={{ color: '#1CB0C6' }}>{feat.icon}</div>
              <h4 className="font-cormorant text-lg font-medium" style={{ color: '#F0FBFC' }}>
                {feat.title}
              </h4>
              <p className="font-outfit text-sm leading-relaxed" style={{ color: '#83A9AF' }}>
                {feat.desc}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="reveal flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <p className="font-cormorant text-2xl font-light italic" style={{ color: '#F0FBFC' }}>
            Advance your implant practice this year.
          </p>
          <button
            data-testid="academy-book-btn"
            onClick={() => onBook('professional')}
            className="flex items-center gap-3 font-outfit text-sm font-medium px-8 py-4 transition-all duration-200 hover:opacity-90 active:scale-95 whitespace-nowrap"
            style={{ background: '#1CB0C6', color: '#082025' }}
          >
            Book Your Course Place
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}
